import { Box, Stack, useMediaQuery, useTheme } from '@mui/material'
import { useForm, FormProvider } from 'react-hook-form'
import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'components/global/CUK/Toast/Toast'
import DatePicker from 'components/global/CUK/DatePicker/DatePicker'
import LoginPaper from '../Login/components/LoginPaper'
import LoginButton from '../Login/components/LoginButton'
import { validationSchema } from './validation'
import { GuestCodeFormValues, SponsorLink } from './types'
import useActions from '../use-actions'
import { format } from 'date-fns'
import AuthBasePageWrapper from '../Login/AuthBasePageWrapper'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  ContentContainer,
  PageTitle,
  RecipientContainer,
  RecipientTitle,
  RecipientInfo,
  DatePickerField,
  DatePickerIcon,
  DateText,
  ButtonContainer,
  LoadingSpinner,
  FormElementsContainer
} from './styles'

const formatCalendarDateAsUtc = (date: Date) => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  ).toISOString()
}

const SponsorCodeGeneratorPanel = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { getSponsorLink, generateSponsorCode } = useActions()
  const { t } = useTranslation()
  const theme = useTheme()
  const isNarrowViewport = useMediaQuery(theme.breakpoints.down('lg'))
  const [submitLoading, setSubmitLoading] = useState(false)
  const [dataLoading, setDataLoading] = useState(false)
  const [sponsorLink, setSponsorLink] = useState<SponsorLink | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [dateSelected, setDateSelected] = useState(false)
  const [dataFetchInitiated, setDataFetchInitiated] = useState(false)

  const methods = useForm<GuestCodeFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema(t)),
    defaultValues: {
      date: null
    }
  })

  const formattedDate = useMemo(() => {
    return selectedDate ? format(selectedDate, 'dd/MM/yyyy') : ''
  }, [selectedDate])

  const fetchData = useCallback(async () => {
    if (!token || dataLoading || sponsorLink || dataFetchInitiated) return

    setDataFetchInitiated(true)
    setDataLoading(true)

    try {
      const data = await getSponsorLink(token)
      if (!data) {
        throw new Error('Invalid sponsor link')
      }
      setSponsorLink(data)
    } catch (error) {
      navigate('/errors/sponsor.error.invalid_link')
      toast.error(t('sponsor.error.invalid_link'))
    } finally {
      setDataLoading(false)
    }
  }, [token, dataLoading, sponsorLink, dataFetchInitiated, getSponsorLink, navigate, t])

  if (token && !dataFetchInitiated && !dataLoading && !sponsorLink) {
    fetchData()
  }

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date)
      setDateSelected(true)
      methods.setValue('date', date, { shouldValidate: true })
    }
  }

  const handleDateSubmit = (date: Date | null) => {
    if (date) {
      setSelectedDate(date)
      setDateSelected(true)
      methods.setValue('date', date, { shouldValidate: true })
    }
  }

  const handleGenerateCode = async () => {
    if (!token || !selectedDate) return
    try {
      setSubmitLoading(true)
      const response = await generateSponsorCode(token, formatCalendarDateAsUtc(selectedDate))

      if (response && response.success) {
        navigate(`/sponsor/${token}/confirm`, { state: { sponsorLink } })
      } else {
        toast.error(t('sponsor.error.generate_code'))
      }
    } catch (error) {
      console.error('Error using sponsor link:', error)
      toast.error(t('sponsor.error.generate_code'))
    } finally {
      setSubmitLoading(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <LoginPaper sx={{ width: 'auto', minHeight: '500px' }}>
        {dataLoading ? (
          <Stack gap={3} sx={{ alignItems: 'center', justifyContent: 'center' }}>
            <PageTitle>{t('sponsor.page.title')}</PageTitle>
            <LoadingSpinner>
              <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '24px' }}></i>
            </LoadingSpinner>
          </Stack>
        ) : (
          <ContentContainer>
            <PageTitle>{t('sponsor.page.title')}</PageTitle>
            {sponsorLink && (
              <>
                <FormElementsContainer>
                  <Stack gap={4}>
                    <RecipientContainer>
                      <RecipientTitle>{t('sponsor.confirmation.body.recipient')}</RecipientTitle>
                      <RecipientInfo>
                        <Box component="span">{`${sponsorLink.first_name} ${sponsorLink.last_name}`}</Box>
                        <Box component="span">-</Box>
                        <Box component="span" sx={{ color: 'primary.main' }}>{sponsorLink.email}</Box>
                      </RecipientInfo>
                    </RecipientContainer>
                    <Box sx={{ width: '100%', mt: 2 }}>
                      <DatePicker
                        placement={isNarrowViewport ? 'bottom' : 'right'}
                        defaultStartDate={selectedDate || undefined}
                        onChange={handleDateChange}
                        onSubmit={handleDateSubmit}
                        blockPastDates={true}
                        singleDateMode={true}
                        field={
                          <DatePickerField>
                            <DatePickerIcon>
                              <i className="fa-regular fa-calendar-range" />
                            </DatePickerIcon>
                            <DateText selected={dateSelected}>
                              {dateSelected ? formattedDate : t('sponsor.page.validity_duration')}
                            </DateText>
                          </DatePickerField>
                        }
                      />
                    </Box>
                  </Stack>
                </FormElementsContainer>
                <ButtonContainer>
                  <LoginButton
                    loading={submitLoading}
                    onClick={handleGenerateCode}
                    disabled={!dateSelected}
                    sx={{ width: '100%' }}
                  >
                    {t('sponsor.page.create_code')}
                  </LoginButton>
                </ButtonContainer>
              </>
            )}
          </ContentContainer>
        )}
      </LoginPaper>
    </FormProvider>
  )
}

const SponsorCodeGeneratorPage = () => {
  return (
    <AuthBasePageWrapper>
      <SponsorCodeGeneratorPanel />
    </AuthBasePageWrapper>
  )
}

export default SponsorCodeGeneratorPage
